import { createClient, SupabaseClient } from '@supabase/supabase-js'
import { authenticateUser as verifyUserCredentials } from '../utils/authHelpers'

// Get environment variables from Vite's import.meta.env
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ""
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ""

let supabase: SupabaseClient | null = null;

// Only create Supabase client if valid URL and key are provided
try {
  if (supabaseUrl && supabaseKey && supabaseUrl.startsWith('https://')) {
    supabase = createClient(supabaseUrl, supabaseKey);
  }
} catch (error) {
  // Supabase client initialization failed - will be handled by null checks
}

// Authentication functions
export interface User {
  id: number;
  username: string;
  is_active: boolean;
}

export async function authenticateUser(username: string, password: string): Promise<User | null> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Get all active users since usernames are hashed
    const { data: allUsers, error } = await supabase
      .from('users')
      .select('id, username, password, salt, is_active')
      .eq('is_active', true);

    if (error || !allUsers || allUsers.length === 0) {
      return null;
    }

    // Find user by testing username/password hash with each salt
    for (const user of allUsers) {
      // First check if user is active before doing expensive hash verification
      if (!user.is_active) {
        continue; // Skip inactive users
      }

      const isAuthenticated = await verifyUserCredentials(username, password, {
        username: user.username,
        password: user.password,
        salt: user.salt
      });

      if (isAuthenticated) {
        // Return user data without sensitive information
        return {
          id: user.id,
          username: username, // Return the original username, not the hash
          is_active: user.is_active
        } as User;
      }
    }

    return null;
  } catch (error) {
    console.error('Authentication error:', error);
    return null;
  }
}

export { supabase };